package at.theatervogelweide.admin.web.screens.news

import at.theatervogelweide.admin.contract.ImageDetails
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.AdminError
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.ErrorCode
import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.ImageCategory
import at.theatervogelweide.admin.contract.VersionedId
import at.theatervogelweide.admin.contract.moved
import at.theatervogelweide.admin.contract.news.NewsDraft
import at.theatervogelweide.admin.contract.news.NewsEditor
import at.theatervogelweide.admin.contract.news.NewsListEntry
import at.theatervogelweide.admin.contract.news.NewsService
import at.theatervogelweide.admin.web.AdminApi
import at.theatervogelweide.admin.web.components.AdminBadge
import at.theatervogelweide.admin.web.components.AdminButton
import at.theatervogelweide.admin.web.components.AdminCard
import at.theatervogelweide.admin.web.components.AdminCardFooterSpacer
import at.theatervogelweide.admin.web.components.AdminCheckbox
import at.theatervogelweide.admin.web.components.AdminConfirmDialog
import at.theatervogelweide.admin.web.components.AdminConflictCallout
import at.theatervogelweide.admin.web.components.AdminDateField
import at.theatervogelweide.admin.web.components.AdminFilterChips
import at.theatervogelweide.admin.web.components.AdminImageUploader
import at.theatervogelweide.admin.web.components.AdminMarkdownField
import at.theatervogelweide.admin.web.components.AdminPageHeader
import at.theatervogelweide.admin.web.components.AdminSelectableList
import at.theatervogelweide.admin.web.components.AdminSplitView
import at.theatervogelweide.admin.web.components.AdminTextField
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.ButtonVariant
import at.theatervogelweide.admin.web.components.EditableImages
import at.theatervogelweide.admin.web.components.FilterOption
import at.theatervogelweide.admin.web.components.LocalPanes
import at.theatervogelweide.admin.web.components.PublicSiteLink
import at.theatervogelweide.admin.web.components.ReorderAnnouncement
import at.theatervogelweide.admin.web.components.ReportUnsavedChanges
import at.theatervogelweide.admin.web.components.SelectableRow
import at.theatervogelweide.admin.web.components.SplitController
import at.theatervogelweide.admin.web.components.UnsavedChangesGuard
import at.theatervogelweide.admin.web.components.editorShown
import at.theatervogelweide.admin.web.components.rememberReorder
import at.theatervogelweide.admin.web.rpcCall
import at.theatervogelweide.domain.NewsVisibility
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.rpc.RpcClient
import kotlinx.rpc.withService
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Clock
import kotlin.time.Duration.Companion.minutes

private val formGrid = Bem("admin-form-grid")
private val button = Bem("admin-button")
private val listPanel = Bem("admin-list-panel")
private val alert = Bem("admin-alert")
private val fieldset = Bem("admin-fieldset")

/** The editor of one item, or of a new one while [loaded] is `null`, whose "Veröffentlicht ab" starts as now (#151). */
private class NewsEditorState(loaded: NewsEditor?, val images: EditableImages) {
    var loaded: NewsEditor? by mutableStateOf(loaded)
    val original: NewsDraft = loaded?.draft ?: NewsDraft(activeFrom = NewsDraft.publishedNow(now()))
    private val initialForm = NewsForm.of(original)

    var form: NewsForm by mutableStateOf(initialForm)
    var fieldErrors: List<FieldError> by mutableStateOf(emptyList())
    var error: String? by mutableStateOf(null)
    var conflict: Boolean by mutableStateOf(false)
    var saving: Boolean by mutableStateOf(false)

    /** "Neu laden" after a conflict is on its way. */
    var reloading: Boolean by mutableStateOf(false)

    /** Nothing may change while the server's answer is about to replace this editor. */
    val busy: Boolean get() = saving || reloading

    val isNew: Boolean get() = loaded == null

    val hasChanges: Boolean
        get() = form != initialForm || images.uuids != original.imageUuids || images.currentDetails != original.imageDetails || images.uploading

    fun errorFor(field: String): String? = fieldErrors.firstOrNull { it.field == field }?.message

    fun showFailure(failure: AdminError) {
        conflict = failure.code == ErrorCode.CONFLICT
        fieldErrors = failure.fieldErrors
        error = when {
            conflict || failure.fieldErrors.isNotEmpty() -> null
            failure.code == ErrorCode.NOT_FOUND -> "Diese Neuigkeit gibt es nicht mehr. Bitte wähle eine andere."
            else -> failure.message
        }
    }
}

private fun now(): LocalDateTime = Clock.System.now().toLocalDateTime(TimeZone.UTC)

/**
 * Neuigkeiten (#21): the list in the public site's order beside the editor of the selected item. [selectedId] comes
 * from the address, and [split] changes it; with panes and no selection the first item opens. [startNew] opens the
 * editor of a new item (`/neuigkeiten/neu`), as the overview's "+ Neuigkeit" does.
 */
@OptIn(FlowPreview::class)
@Composable
fun NewsScreen(
    rpc: RpcClient,
    api: AdminApi,
    guard: UnsavedChangesGuard,
    split: SplitController,
    selectedId: Long?,
    startNew: Boolean,
) {
    val service = remember(rpc) { rpc.withService<NewsService>() }
    val scope = rememberCoroutineScope()

    var entries by remember { mutableStateOf<List<NewsListEntry>?>(null) }
    // the order shown, which runs ahead of the server's while a reorder is on its way
    var order by remember { mutableStateOf(emptyList<Long>()) }
    var listError by remember { mutableStateOf<String?>(null) }
    var filter by remember { mutableStateOf<NewsVisibility?>(null) }
    var editor by remember { mutableStateOf<NewsEditorState?>(null) }
    var editorError by remember { mutableStateOf<String?>(null) }
    var creating by remember { mutableStateOf(startNew) }
    var confirmDelete by remember { mutableStateOf(false) }
    val reorderRequests = remember { MutableStateFlow<List<Long>?>(null) }
    // a moved order the server hasn't confirmed yet; list reloads keep it instead of the order they read
    var reorderPending by remember { mutableStateOf(false) }

    fun newEditor(loaded: NewsEditor?): NewsEditorState {
        editor?.images?.dispose()
        return NewsEditorState(loaded, newImages(scope, api, loaded?.draft?.imageUuids.orEmpty(), loaded?.draft?.imageDetails.orEmpty()))
    }

    suspend fun reloadList() {
        when (val result = rpcCall { service.list() }) {
            is AdminResult.Success -> {
                entries = result.value
                val ids = result.value.map { it.id }
                order = if (reorderPending) order.filter { it in ids } + ids.filterNot { it in order } else ids
                listError = null
            }

            is AdminResult.Failure -> listError = result.error.message
        }
    }

    /** Loads [id] in place of [replacing]; the answer is dropped if another editor opened meanwhile. */
    suspend fun loadEditor(id: Long, replacing: NewsEditorState? = editor) {
        val result = rpcCall { service.load(id) }
        if (editor !== replacing) {
            return
        }
        when (result) {
            is AdminResult.Success -> {
                editor = newEditor(result.value)
                editorError = null
            }

            is AdminResult.Failure -> {
                editor?.images?.dispose()
                editor = null
                editorError = if (result.error.code == ErrorCode.NOT_FOUND) "Diese Neuigkeit gibt es nicht mehr." else result.error.message
            }
        }
    }

    LaunchedEffect(service) { reloadList() }

    LaunchedEffect(service, selectedId) {
        if (selectedId != null && editor?.loaded?.id != selectedId) {
            creating = false
            loadEditor(selectedId)
        }
    }

    // with panes and without a selection, the first item opens
    val panes = LocalPanes.current
    LaunchedEffect(entries, selectedId, creating, panes) {
        val first = entries?.firstOrNull()
        if (panes && selectedId == null && !creating && first != null) {
            split.show(first.id)
        }
    }

    // the editor of a new item follows the address: /neuigkeiten/neu opens it, leaving it for /neuigkeiten closes it
    LaunchedEffect(startNew) {
        when {
            startNew && editor?.isNew != true -> {
                creating = true
                editorError = null
                editor = newEditor(null)
            }

            !startNew && creating && selectedId == null -> {
                editor?.images?.dispose()
                editor = null
                creating = false
            }
        }
    }

    // back on the list alone, the editor and its changes are gone
    val editorVisible = editorShown(selectedId, startNew)
    LaunchedEffect(editorVisible) {
        if (!editorVisible) {
            editor?.images?.dispose()
            editor = null
            editorError = null
            creating = false
        }
    }

    // reorders are sent one after another, each with the versions of the latest list
    LaunchedEffect(service) {
        reorderRequests.filterNotNull().debounce(400).collect { requested ->
            val versions = entries.orEmpty().associate { it.id to it.version }
            when (val result = rpcCall { service.reorder(requested.map { VersionedId(it, versions[it] ?: -1) }) }) {
                is AdminResult.Success -> {
                    entries = result.value
                    // later moves are still on their way unless the shown order is the confirmed one
                    reorderPending = order != result.value.map { it.id }
                    listError = null
                    // the open item moved too; its new version is the one to save against
                    editor?.let { state ->
                        val loaded = state.loaded ?: return@let
                        val after = result.value.firstOrNull { it.id == loaded.id }?.version ?: return@let
                        if (versions[loaded.id] == loaded.version) {
                            state.loaded = loaded.copy(version = after)
                        }
                    }
                }

                is AdminResult.Failure -> {
                    listError = if (result.error.code == ErrorCode.CONFLICT) {
                        "Die Liste hat sich inzwischen geändert. Sie ist jetzt neu geladen; bitte verschiebe noch einmal."
                    } else {
                        "Die Reihenfolge konnte nicht gespeichert werden. ${result.error.message}"
                    }
                    reorderPending = false
                    reloadList()
                }
            }
        }
    }

    val state = editor.takeIf { editorVisible }
    val hasChanges = state?.hasChanges == true
    ReportUnsavedChanges(guard, hasChanges)

    fun select(id: Long) {
        if (id == selectedId && !creating) {
            return
        }
        guard.proceed {
            creating = false
            if (editor?.loaded?.id != id) {
                editor?.images?.dispose()
                editor = null
            }
            split.open(id)
        }
    }

    fun startNew() {
        guard.proceed {
            creating = true
            editorError = null
            editor = newEditor(null)
            split.openNew()
        }
    }

    fun save() {
        val current = editor ?: return
        val (draft, errors) = current.form.toDraft(current.original, current.images.uuids, current.images.detailsToSave, current.isNew)
        current.fieldErrors = errors
        current.error = null
        if (draft == null || current.saving) {
            return
        }

        current.saving = true
        scope.launch {
            val loaded = current.loaded
            val result = rpcCall { if (loaded == null) service.create(draft) else service.save(loaded.versionedId, draft) }
            current.saving = false
            // the person may have switched to another item meanwhile; then only the list shows the result
            if (editor !== current) {
                reloadList()
                return@launch
            }
            when (result) {
                is AdminResult.Success -> {
                    editor = newEditor(result.value)
                    creating = false
                    split.show(result.value.id)
                    reloadList()
                }

                is AdminResult.Failure -> current.showFailure(result.error)
            }
        }
    }

    fun discard() {
        val current = editor ?: return
        val loaded = current.loaded
        if (loaded == null) {
            current.images.dispose()
            editor = null
            creating = false
            split.backToList()
        } else {
            editor = newEditor(loaded)
        }
    }

    fun reloadAfterConflict() {
        val current = editor ?: return
        val id = current.loaded?.id ?: return
        if (current.reloading) {
            return
        }
        current.reloading = true
        scope.launch {
            try {
                loadEditor(id, replacing = current)
            } finally {
                current.reloading = false
            }
            reloadList()
        }
    }

    fun delete() {
        confirmDelete = false
        val current = editor ?: return
        val loaded = current.loaded ?: return
        scope.launch {
            val result = rpcCall { service.delete(loaded.versionedId) }
            if (editor !== current) {
                reloadList()
                return@launch
            }
            when (result) {
                is AdminResult.Success -> {
                    current.images.dispose()
                    editor = null
                    // gone from the list before the selection moves on, so the first remaining item opens
                    entries = entries?.filterNot { it.id == loaded.id }
                    order = order - loaded.id
                    split.backToList()
                    reloadList()
                }

                is AdminResult.Failure -> current.showFailure(result.error)
            }
        }
    }

    AdminPageHeader("Neuigkeiten", unsavedChanges = hasChanges) {
        AdminButton(variant = ButtonVariant.PRIMARY, onClick = ::startNew) { Text("+ Neuigkeit") }
    }

    // a state that a timer moves on, so badges, counts and filters follow when an item starts or ends
    var now by remember { mutableStateOf(now()) }
    LaunchedEffect(Unit) {
        while (true) {
            delay(1.minutes)
            now = now()
        }
    }
    val byId = entries.orEmpty().associateBy { it.id }
    val ordered = order.mapNotNull { byId[it] }
    val shown = ordered.filter { filter == null || it.visibility(now) == filter }
    val reorder = rememberReorder(
        keys = ordered.map { it.id },
        describe = { id -> byId[id]?.title ?: "Neuigkeit" },
    ) { from, to ->
        order = order.moved(from, to)
        reorderPending = true
        reorderRequests.value = order
    }

    AdminSplitView(
        controller = split,
        selectedId = selectedId,
        new = startNew,
        name = "Neuigkeiten",
        listLabel = "Liste der Neuigkeiten",
        list = {
            Div({ classes(listPanel.element("filters").className) }) {
                AdminFilterChips(
                    options = listOf(FilterOption<NewsVisibility?>(null, "Alle", ordered.size)) +
                        NewsVisibility.entries.map { visibility ->
                            FilterOption(visibility, visibility.label, ordered.count { it.visibility(now) == visibility })
                            },
                    selected = filter,
                    onSelect = { filter = it },
                )
            }

            listError?.let {
                P({
                    classes(listPanel.element("message").className, alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }

            when {
                entries == null -> P({ classes(listPanel.element("message").className) }) { Text("Lade …") }
                ordered.isEmpty() -> P({ classes(listPanel.element("message").className) }) {
                    Text("Noch keine Neuigkeiten. Lege mit „+ Neuigkeit“ die erste an.")
                }

                shown.isEmpty() -> P({ classes(listPanel.element("message").className) }) {
                    Text("Keine Neuigkeiten mit diesem Status.")
                }

                else -> AdminSelectableList(
                    rows = shown.map { entry ->
                        val visibility = entry.visibility(now)
                        SelectableRow(
                            key = entry.id,
                            title = entry.title,
                            meta = listOfNotNull(entry.periodText(now), entry.authorName).joinToString(" · "),
                            thumbnailUrl = entry.mainImageUuid?.let { api.imageUrl(ImageCategory.NEWS_ITEMS, it) },
                            badge = { AdminBadge(visibility.label, visibility.badgeVariant) },
                        )
                        },
                    selected = if (creating) null else selectedId,
                    onSelect = ::select,
                    recent = split.recent,
                    // sorting a filtered list would hide where an item lands among the others
                    reorder = reorder.takeIf { filter == null && ordered.size > 1 },
                )
            }
            ReorderAnnouncement(reorder)
        },
        detail = {
            when {
                state != null -> NewsEditorCard(
                    state = state,
                    api = api,
                    now = now,
                    onSave = ::save,
                    onDiscard = ::discard,
                    onDelete = { confirmDelete = true },
                    onReload = ::reloadAfterConflict,
                )

                editorError != null -> P({
                    classes(alert.block)
                    attr("role", "alert")
                }) { Text(editorError!!) }
            }
        },
    )

    AdminConfirmDialog(
        open = confirmDelete,
        id = "neuigkeit-loeschen",
        title = "Neuigkeit löschen?",
        message = "„${state?.loaded?.draft?.title.orEmpty()}“ verschwindet mit ihren Bildern von der Website. " +
            "Das lässt sich nicht rückgängig machen.",
        confirmLabel = "Löschen",
        cancelLabel = "Behalten",
        onConfirm = ::delete,
        onCancel = { confirmDelete = false },
        danger = true,
    )
}

private fun newImages(scope: CoroutineScope, api: AdminApi, uuids: List<String>, details: Map<String, ImageDetails>) =
    EditableImages(scope, { file -> api.uploadImage(ImageCategory.NEWS_ITEMS, file) }, uuids, details = details)

@Composable
private fun NewsEditorCard(
    state: NewsEditorState,
    api: AdminApi,
    now: LocalDateTime,
    onSave: () -> Unit,
    onDiscard: () -> Unit,
    onDelete: () -> Unit,
    onReload: () -> Unit,
) {
    val form = state.form
    val visibility = form.visibility(state.original, now)

    fun update(change: NewsForm.() -> NewsForm) {
        state.form = state.form.change()
    }

    Form(attrs = {
        attr("novalidate", "")
        onSubmit { event ->
            event.preventDefault()
            onSave()
        }
    }) {
        AdminCard(
            title = if (state.isNew) "Neue Neuigkeit" else "Neuigkeit bearbeiten",
            id = "neuigkeit",
            header = { AdminBadge(visibility.label, visibility.badgeVariant) },
            footer = if (state.isNew) {
                null
            } else {
                {
                    AdminButton(variant = ButtonVariant.DANGER, disabled = state.busy, onClick = onDelete) { Text("Löschen") }
                    AdminCardFooterSpacer()
                    // the public site shows its news on the start page
                    PublicSiteLink("/", button.block, button.modifier("link")) { Text("Auf Website ansehen ↗") }
                }
            },
            saveActions = {
                AdminButton(disabled = state.busy || (!state.hasChanges && !state.isNew), onClick = onDiscard) { Text("Verwerfen") }
                AdminButton(
                    variant = ButtonVariant.PRIMARY,
                    type = ButtonType.Submit,
                    disabled = state.busy || state.images.uploading || (!state.hasChanges && !state.isNew),
                ) { Text(if (state.saving) "Speichert …" else "Speichern") }
            },
        ) {
            if (state.conflict) {
                AdminConflictCallout(onReload = onReload)
            }
            state.error?.let {
                P({
                    classes(alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }

            // nothing changes while saving or reloading, so the answer can replace the editor without dropping input
            Fieldset({
                classes(fieldset.block)
                if (state.busy) {
                    attr("disabled", "")
                }
            }) {
                AdminTextField(
                    id = "news-${NewsDraft.TITLE}",
                    label = "Titel",
                    value = form.title,
                    onValueChange = { update { copy(title = it) } },
                    error = state.errorFor(NewsDraft.TITLE),
                )
                AdminMarkdownField(
                    id = "news-${NewsDraft.TEXT}",
                    label = "Text",
                    value = form.text,
                    onValueChange = { update { copy(text = it) } },
                    error = state.errorFor(NewsDraft.TEXT),
                )
                Div({ classes(formGrid.block) }) {
                    AdminDateField(
                        id = "news-${NewsDraft.ACTIVE_FROM}",
                        label = "Veröffentlicht ab",
                        text = form.fromText,
                        onTextChange = { update { copy(fromText = it) } },
                        error = state.errorFor(NewsDraft.ACTIVE_FROM),
                        hint = form.fromHint(state.original, state.isNew),
                        // older items without a date can still be saved, so only a new one needs it
                        required = state.isNew,
                    )
                    AdminDateField(
                        id = "news-${NewsDraft.ACTIVE_TO}",
                        label = "Veröffentlicht bis",
                        text = form.toText,
                        onTextChange = { update { copy(toText = it) } },
                        error = state.errorFor(NewsDraft.ACTIVE_TO),
                        hint = form.toHint(state.original),
                        optional = true,
                    )
                }
                AdminImageUploader(
                    id = "news-${NewsDraft.IMAGES}",
                    label = "Bilder",
                    images = state.images,
                    imageUrl = { api.imageUrl(ImageCategory.NEWS_ITEMS, it) },
                    compact = true,
                    disabled = state.busy,
                    error = state.errorFor(NewsDraft.IMAGES),
                )
                state.loaded?.let { loaded ->
                    AdminCheckbox(
                        label = "Unter meinem Namen veröffentlichen",
                        checked = form.publishUnderMyName,
                        onCheckedChange = { checked -> update { copy(publishUnderMyName = checked) } },
                        note = "(aktuell: ${loaded.authorName ?: "ohne Namen"})",
                    )
                }
            }
        }
    }
}
